import React, { useState, useEffect } from 'react';
import { Wrench, AlertTriangle, Clock, CheckCircle, ChevronRight } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

export default function EquipmentMaintenanceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiRequest('/store-ops/equipment/widget');
        setData(result);
      } catch (error) {
        console.error('Failed to load equipment data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="equipment-maintenance-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Equipment Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-8 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};

  return (
    <Card data-testid="equipment-maintenance-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-blue-600" />
            Equipment Maintenance
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/equipment')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold">{summary.total_equipment || 0}</div>
            <div className="text-xs text-slate-500">Total Equipment</div>
          </div>
          <div className={`p-3 rounded-lg text-center ${summary.overdue_maintenance > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <div className={`text-2xl font-bold ${summary.overdue_maintenance > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {summary.overdue_maintenance || 0}
            </div>
            <div className="text-xs text-slate-500">Overdue</div>
          </div>
        </div>

        {/* Alerts */}
        {summary.overdue_maintenance > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-red-500" />
              Overdue Maintenance
            </div>
            {(data?.overdue_maintenance || []).slice(0, 3).map((eq, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                <div>
                  <span className="font-medium">{eq.name}</span>
                  <span className="text-slate-500 ml-2">{eq.store}</span>
                </div>
                <Badge variant="destructive" className="text-xs">
                  {eq.days_overdue}d overdue
                </Badge>
              </div>
            ))}
          </div>
        )}

        {summary.due_soon > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-600 flex items-center gap-1">
              <Clock className="h-3 w-3 text-yellow-500" />
              Due Soon
            </div>
            {(data?.due_soon || []).slice(0, 3).map((eq, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-yellow-50 rounded text-sm">
                <div>
                  <span className="font-medium">{eq.name}</span>
                  <span className="text-slate-500 ml-2">{eq.store}</span>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                  {eq.days_until}d left
                </Badge>
              </div>
            ))}
          </div>
        )}

        {summary.under_repair > 0 && (
          <div className="flex items-center justify-between p-2 bg-orange-50 rounded text-sm">
            <span className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-orange-600" />
              Under Repair
            </span>
            <Badge className="bg-orange-100 text-orange-800">{summary.under_repair}</Badge>
          </div>
        )}

        {summary.warranty_expiring > 0 && (
          <div className="flex items-center justify-between p-2 bg-purple-50 rounded text-sm">
            <span>Warranty Expiring Soon</span>
            <Badge className="bg-purple-100 text-purple-800">{summary.warranty_expiring}</Badge>
          </div>
        )}

        {summary.overdue_maintenance === 0 && summary.due_soon === 0 && summary.under_repair === 0 && (
          <div className="text-center py-4 text-green-600">
            <CheckCircle className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">All equipment up to date</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
